import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch,
  faFilter, 
  faSpinner,
  faFileAlt,
  faChevronLeft,
  faChevronRight,
  faFileExcel,
  faClock,
  faCheckSquare,
  faSquare,
  faTimes,
  faSave
} from '@fortawesome/free-solid-svg-icons';
import Pagination from '../../components/Pagination';
import { getHistoricoNotasAluno } from '../../services/lancamentoNotasService';
import cursoService from '../../services/cursoService';
import produtoService from '../../services/produtoService';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/HistoricoNotas.css';

function HistoricoNotas() {
  // Permissões
  const { hasPermission } = useAuth();
  const podeAlterarCargaHorariaMassa = hasPermission('academico.notas.carga_horaria_massa.update');

  // Estados de filtros
  const [selectedExtensao, setSelectedExtensao] = useState('');
  const [selectedAluno, setSelectedAluno] = useState('');
  const [selectedCurso, setSelectedCurso] = useState('');
  const [selectedDisciplina, setSelectedDisciplina] = useState('');
  // Extensão em que a nota foi lançada (diferente da extensão atual do aluno
  // quando ele já foi transferido de extensão).
  const [selectedExtensaoLancamento, setSelectedExtensaoLancamento] = useState('');

  // Estados de dados
  const [extensoes, setExtensoes] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [disciplinas, setDisciplinas] = useState([]);
  const [notas, setNotas] = useState([]);
  const [extensoesLancamento, setExtensoesLancamento] = useState([]);

  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showFilters, setShowFilters] = useState(true);

  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalNotas, setTotalNotas] = useState(0);

  // Estados para edição em massa de carga horária
  const [selectedIds, setSelectedIds] = useState([]);
  const [showCargaHorariaModal, setShowCargaHorariaModal] = useState(false);
  const [novaCargaHoraria, setNovaCargaHoraria] = useState('');
  const [salvandoCargaHoraria, setSalvandoCargaHoraria] = useState(false);

  const pageSizes = [10, 20, 50, 100];

  // Mensagem
  const mostrarMensagem = useCallback((texto, tipo = 'info') => {
    setMessage({ texto, tipo });
    setTimeout(() => setMessage(null), 5000);
  }, []);

  // Carregar extensões do usuário logado
  const carregarExtensoes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/usuarios/me/extensoes');
      const extensoesData = Array.isArray(response.data) ? response.data : [];
      setExtensoes(extensoesData);
      if (extensoesData.length === 0) {
        mostrarMensagem('Você não tem permissão em nenhuma extensão', 'warning');
      }
    } catch (error) {
      console.error('Erro ao carregar extensões:', error);
      mostrarMensagem('Erro ao carregar extensões', 'error');
      setExtensoes([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  // Carregar alunos por extensão
  const carregarAlunos = useCallback(async (extensaoId) => {
    if (!extensaoId) {
      setAlunos([]);
      return;
    }

    try {
      setLoading(true);
      // Usar rota /alunos que já filtra por dtype='Aluno' e passar limite alto
      const response = await api.get('/alunos', {
        params: {
          extensoes: extensaoId,
          limit: 5000  // Limite alto para trazer todos os alunos
        }
      });
      
      const alunosData = response.data?.data || response.data?.pessoas || [];
      // Ordenar alfabeticamente pelo nome
      const alunosOrdenados = Array.isArray(alunosData) 
        ? alunosData.sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'))
        : [];
      setAlunos(alunosOrdenados);
    } catch (error) {
      console.error('Erro ao carregar alunos:', error);
      mostrarMensagem('Erro ao carregar alunos', 'error');
      setAlunos([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  // Carregar cursos
  const carregarCursos = useCallback(async () => {
    try {
      const response = await cursoService.list();
      const cursosData = response?.data?.cursos || response?.cursos || response?.data || [];
      setCursos(Array.isArray(cursosData) ? cursosData : []);
    } catch (error) {
      console.error('Erro ao carregar cursos:', error);
      setCursos([]);
    }
  }, []);

  // Carregar disciplinas
  const carregarDisciplinas = useCallback(async () => {
    try {
      const response = await produtoService.list({ tipoProduto: 1 }); // 1 = Disciplina
      const disciplinasData = response?.data?.produtos || response?.produtos || response?.data || [];
      setDisciplinas(Array.isArray(disciplinasData) ? disciplinasData : []);
    } catch (error) {
      console.error('Erro ao carregar disciplinas:', error);
      setDisciplinas([]);
    }
  }, []);

  // Carregar notas do aluno
  const carregarNotas = useCallback(async (page = 1) => {
    if (!selectedAluno) {
      setNotas([]);
      return;
    }

    try {
      setLoading(true);
      
      const filters = {
        page,
        limit: itemsPerPage
      };
      
      // A extensão do topo serve para escolher o aluno; ela não filtra as notas.
      // O histórico de um aluno transferido inclui as notas lançadas nas
      // extensões anteriores.
      if (selectedExtensaoLancamento) filters.extensao = selectedExtensaoLancamento;
      if (selectedCurso) filters.curso = selectedCurso;
      if (selectedDisciplina) filters.disciplina = selectedDisciplina;

      console.log('📚 Carregando notas do aluno:', selectedAluno, filters);

      const response = await getHistoricoNotasAluno(selectedAluno, filters);
      
      setNotas(response.notas || []);
      setExtensoesLancamento(response.extensoesLancamento || []);
      setTotalNotas(response.pagination?.total || 0);
      
      console.log(`✅ ${response.notas?.length || 0} notas carregadas`);
      
    } catch (error) {
      console.error('Erro ao carregar notas:', error);
      mostrarMensagem('Erro ao carregar notas do aluno', 'error');
      setNotas([]);
    } finally {
      setLoading(false);
    }
  }, [selectedAluno, selectedExtensaoLancamento, selectedCurso, selectedDisciplina, itemsPerPage, mostrarMensagem]);

  // Handler para mudança de itens por página
  const handlePageSizeChange = (newSize) => {
    setItemsPerPage(newSize);
    setCurrentPage(1); // Resetar para primeira página
  };

  // Função para exportar para Excel
  const exportarParaExcel = () => {
    if (notas.length === 0) {
      mostrarMensagem('Não há notas para exportar', 'warning');
      return;
    }

    try {
      // Criar cabeçalho
      const headers = ['Código', 'Extensão do lançamento', 'Curso', 'Disciplina', 'Aluno', 'Carga Horária', 'Nota', 'Status', 'Lançada em', 'Lançada por', 'Alterada em', 'Alterada por'];
      
      // Mapear dados
      const data = notas.map(nota => [
        nota.codigoDisciplina || '',
        nota.nomeExtensao || '',
        nota.nomeCurso || '',
        nota.nomeDisciplina || '',
        nota.nomeAluno || '',
        nota.cargaHoraria ? `${nota.cargaHoraria}h` : '',
        nota.nota !== null ? parseFloat(nota.nota).toFixed(2) : '',
        getNoteStatus(nota.nota) === 'aprovado' ? 'Aprovado' : 
          getNoteStatus(nota.nota) === 'recuperacao' ? 'Recuperação' : 'Reprovado',
        formatarDataHora(nota.dataCadastro),
        nota.pessoaCadastrou || '',
        nota.dataAlteracao ? formatarDataHora(nota.dataAlteracao) : '',
        nota.pessoaAlterou || ''
      ]);

      // Criar CSV
      const csvContent = [
        headers.join(';'),
        ...data.map(row => row.join(';'))
      ].join('\n');

      // Criar blob e download
      const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const alunoNome = notas[0]?.nomeAluno || 'aluno';
      const fileName = `historico_notas_${alunoNome.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      mostrarMensagem('Exportação concluída com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao exportar:', error);
      mostrarMensagem('Erro ao exportar dados', 'error');
    }
  };

  // ========== FUNÇÕES PARA EDIÇÃO EM MASSA DE CARGA HORÁRIA ==========

  // Toggle seleção de um item
  const toggleSelectItem = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(i => i !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // Selecionar/Desselecionar todos da página atual
  const toggleSelectAll = () => {
    const idsNaPagina = notas.map(n => n.id);
    const todosJaSelecionados = idsNaPagina.every(id => selectedIds.includes(id));
    
    if (todosJaSelecionados) {
      // Desselecionar todos da página
      setSelectedIds(prev => prev.filter(id => !idsNaPagina.includes(id)));
    } else {
      // Selecionar todos da página
      setSelectedIds(prev => {
        const novosIds = idsNaPagina.filter(id => !prev.includes(id));
        return [...prev, ...novosIds];
      });
    }
  };

  // Limpar seleção
  const limparSelecao = () => {
    setSelectedIds([]);
  };

  // Abrir modal de carga horária
  const abrirModalCargaHoraria = () => {
    if (selectedIds.length === 0) {
      mostrarMensagem('Selecione pelo menos um registro', 'warning');
      return;
    }
    setNovaCargaHoraria('');
    setShowCargaHorariaModal(true);
  };

  // Salvar carga horária em massa
  const salvarCargaHorariaMassa = async () => {
    if (!novaCargaHoraria || isNaN(novaCargaHoraria) || novaCargaHoraria < 1) {
      mostrarMensagem('Informe uma carga horária válida (mínimo 1 hora)', 'warning');
      return;
    }

    try {
      setSalvandoCargaHoraria(true);
      
      const response = await api.put('/lancamento-notas/carga-horaria/massa', {
        ids: selectedIds,
        cargaHoraria: parseInt(novaCargaHoraria)
      });

      if (response.data?.success) {
        mostrarMensagem(response.data.message || 'Carga horária atualizada com sucesso!', 'success');
        setShowCargaHorariaModal(false);
        setSelectedIds([]);
        // Recarregar notas para mostrar valores atualizados
        carregarNotas(currentPage);
      } else {
        mostrarMensagem(response.data?.error || 'Erro ao atualizar carga horária', 'error');
      }
    } catch (error) {
      console.error('Erro ao atualizar carga horária:', error);
      mostrarMensagem(error.response?.data?.error || 'Erro ao atualizar carga horária', 'error');
    } finally {
      setSalvandoCargaHoraria(false);
    }
  };

  // Verificar se todos da página estão selecionados
  const todosNaPaginaSelecionados = notas.length > 0 && notas.every(n => selectedIds.includes(n.id));

  // Carregar dados iniciais
  useEffect(() => {
    carregarExtensoes();
    carregarCursos();
    carregarDisciplinas();
  }, [carregarExtensoes, carregarCursos, carregarDisciplinas]);

  // Carregar alunos quando extensão mudar
  useEffect(() => {
    if (selectedExtensao) {
      carregarAlunos(selectedExtensao);
      setSelectedAluno(''); // Limpar aluno selecionado
    } else {
      setAlunos([]);
      setSelectedAluno('');
    }
  }, [selectedExtensao, carregarAlunos]);

  // As extensões de lançamento são as do aluno selecionado; trocar de aluno
  // invalida tanto a lista quanto o filtro escolhido.
  useEffect(() => {
    setSelectedExtensaoLancamento('');
    if (!selectedAluno) setExtensoesLancamento([]);
  }, [selectedAluno]);

  // Carregar notas quando aluno ou filtros mudarem
  useEffect(() => {
    if (selectedAluno) {
      setCurrentPage(1);
      setSelectedIds([]); // Limpar seleção ao mudar filtros
      carregarNotas(1);
    } else {
      setNotas([]);
      setSelectedIds([]);
    }
  }, [selectedAluno, selectedCurso, selectedDisciplina, selectedExtensaoLancamento, carregarNotas]);

  // Carregar nova página
  useEffect(() => {
    if (selectedAluno && currentPage > 1) {
      carregarNotas(currentPage);
    }
  }, [currentPage, selectedAluno, carregarNotas]);

  // Paginação
  const totalPages = Math.ceil(totalNotas / itemsPerPage);

  return (
    <div className="historico-notas-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Histórico de Notas</h1>
          <p className="page-subtitle">Consulte todas as notas de um aluno</p>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="btn-filter"
          title="Filtros"
        >
          <FontAwesomeIcon icon={faFilter} />
          <span className="btn-text">Filtros</span>
        </button>
      </div>

      {/* Mensagem */}
      {message && (
        <div className={`alert alert-${message.tipo}`}>
          {message.texto}
        </div>
      )}

      {/* Filtros */}
      {showFilters && (
        <div className="filters-section">
          <div className="filters-grid">
            {/* Extensão atual do aluno — usada para localizar o aluno */}
            <div className="form-group">
              <label>Extensão atual do aluno *</label>
              <select
                value={selectedExtensao}
                onChange={(e) => setSelectedExtensao(e.target.value)}
                className="form-control"
                disabled={loading}
              >
                <option value="">Selecione uma extensão</option>
                {extensoes.map(ext => (
                  <option key={ext.id} value={ext.id}>
                    {ext.nome}
                  </option>
                ))}
              </select>
            </div>

            {/* Aluno */}
            <div className="form-group">
              <label>Aluno *</label>
              <select
                value={selectedAluno}
                onChange={(e) => setSelectedAluno(e.target.value)}
                className="form-control"
                disabled={loading || !selectedExtensao || alunos.length === 0}
              >
                <option value="">
                  {!selectedExtensao 
                    ? 'Selecione uma extensão primeiro' 
                    : alunos.length === 0 
                    ? 'Nenhum aluno encontrado' 
                    : 'Selecione um aluno'}
                </option>
                {alunos.map(aluno => (
                  <option key={aluno.idPessoa} value={aluno.idPessoa}>
                    {aluno.nome} {aluno.cpf ? `- ${aluno.cpf}` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Extensão do lançamento (Opcional) */}
            <div className="form-group">
              <label>Extensão do lançamento (opcional)</label>
              <select
                value={selectedExtensaoLancamento}
                onChange={(e) => setSelectedExtensaoLancamento(e.target.value)}
                className="form-control"
                disabled={loading || !selectedAluno || extensoesLancamento.length === 0}
                title="Extensão em que cada nota foi lançada"
              >
                <option value="">Todas as extensões</option>
                {extensoesLancamento.map(ext => (
                  <option key={ext.codigoExtensao} value={ext.codigoExtensao}>
                    {ext.nomeExtensao}
                  </option>
                ))}
              </select>
            </div>

            {/* Curso (Opcional) */}
            <div className="form-group">
              <label>Curso (opcional)</label>
              <select
                value={selectedCurso}
                onChange={(e) => setSelectedCurso(e.target.value)}
                className="form-control"
                disabled={loading || !selectedAluno}
              >
                <option value="">Todos os cursos</option>
                {cursos.map(curso => (
                  <option key={curso.idCurso} value={curso.idCurso}>
                    {curso.nome}
                  </option>
                ))}
              </select>
            </div>

            {/* Disciplina (Opcional) */}
            <div className="form-group">
              <label>Disciplina (opcional)</label>
              <select
                value={selectedDisciplina}
                onChange={(e) => setSelectedDisciplina(e.target.value)}
                className="form-control"
                disabled={loading || !selectedAluno}
              >
                <option value="">Todas as disciplinas</option>
                {disciplinas.map(disc => (
                  <option key={disc.idProduto} value={disc.idProduto}>
                    {disc.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Orientação */}
      {!selectedAluno && (
        <div className="empty-state">
          <FontAwesomeIcon icon={faFileAlt} size="3x" style={{ opacity: 0.3, marginBottom: '20px' }} />
          <p>Selecione uma extensão e um aluno para visualizar o histórico de notas</p>
        </div>
      )}

      {/* Tabela de Notas */}
      {selectedAluno && (
        <div className="notas-section">
          {loading ? (
            <div className="loading-state">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p>Carregando notas...</p>
            </div>
          ) : notas.length === 0 ? (
            <div className="empty-state">
              <p>Nenhuma nota encontrada para este aluno</p>
            </div>
          ) : (
            <>
              {/* Barra de controles */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
                {/* Paginação (se houver mais de 1 página) */}
                {totalPages > 1 && (
                  <div className="order-2 sm:order-1 w-full sm:w-auto">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalItems={totalNotas}
                      itemsPerPage={itemsPerPage}
                      onPageChange={setCurrentPage}
                    />
                  </div>
                )}
                
                {/* Controles (sempre visível) */}
                <div className={`${totalPages > 1 ? 'order-1 sm:order-2' : ''} flex items-center w-full sm:w-auto justify-end gap-2 flex-wrap`}>
                  <span className="text-sm text-gray-700 whitespace-nowrap">
                    {totalNotas || 0} {totalPages > 1 ? `registros | Página ${currentPage || 1} de ${totalPages || 1}` : 'registros'}
                  </span>
                  <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap mr-2">Por página:</label>
                  <select
                    id="pageSize"
                    value={itemsPerPage}
                    onChange={e => handlePageSizeChange(Number(e.target.value))}
                    className="px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    {pageSizes.map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={exportarParaExcel}
                    className="flex items-center gap-2 px-3 py-1 rounded bg-green-600 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
                    title="Exportar para Excel"
                  >
                    <FontAwesomeIcon icon={faFileExcel} />
                    <span className="hidden sm:inline">Excel</span>
                  </button>
                </div>
              </div>

              {/* Barra de ações em massa - Apenas para quem tem permissão */}
              {podeAlterarCargaHorariaMassa && (
                <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    className="flex items-center gap-2 px-3 py-1.5 rounded bg-gray-200 text-gray-700 font-medium hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
                    title={todosNaPaginaSelecionados ? "Desmarcar todos" : "Selecionar todos da página"}
                  >
                    <FontAwesomeIcon icon={todosNaPaginaSelecionados ? faCheckSquare : faSquare} />
                    <span className="hidden sm:inline">{todosNaPaginaSelecionados ? "Desmarcar" : "Selecionar"} página</span>
                  </button>

                  {selectedIds.length > 0 && (
                    <>
                      <span className="text-sm text-blue-600 font-medium">
                        {selectedIds.length} selecionado(s)
                      </span>
                      <button
                        type="button"
                        onClick={limparSelecao}
                        className="flex items-center gap-1 px-2 py-1 text-sm text-gray-500 hover:text-gray-700"
                        title="Limpar seleção"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                        Limpar
                      </button>
                      <div className="h-6 w-px bg-gray-300 mx-1"></div>
                      <button
                        type="button"
                        onClick={abrirModalCargaHoraria}
                        className="flex items-center gap-2 px-3 py-1.5 rounded bg-blue-600 text-white font-medium shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        title="Alterar carga horária dos selecionados"
                      >
                        <FontAwesomeIcon icon={faClock} />
                        <span className="hidden sm:inline">Alterar Carga Horária</span>
                        <span className="sm:hidden">CH</span>
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Tabela Responsiva */}
              <div className="table-responsive">
                <table className="notas-table historico-notas-table">
                  <thead>
                    <tr>
                      {podeAlterarCargaHorariaMassa && (
                        <th data-col="selecao" className="w-10 text-center">
                          <button
                            type="button"
                            onClick={toggleSelectAll}
                            className="p-1 hover:bg-gray-200 rounded"
                            title={todosNaPaginaSelecionados ? "Desmarcar todos" : "Selecionar todos"}
                          >
                            <FontAwesomeIcon 
                              icon={todosNaPaginaSelecionados ? faCheckSquare : faSquare} 
                              className={todosNaPaginaSelecionados ? "text-blue-600" : "text-gray-400"}
                            />
                          </button>
                        </th>
                      )}
                      <th data-col="codigo">Código</th>
                      <th data-col="extensao">Extensão do lançamento</th>
                      <th data-col="curso">Curso</th>
                      <th data-col="disciplina">Disciplina</th>
                      <th data-col="carga" className="desktop-only">Carga Horária</th>
                      <th data-col="nota">Nota</th>
                      <th data-col="lancada-em">Lançada em</th>
                      <th data-col="lancada-por">Lançada por</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notas.map((nota, index) => (
                      <tr 
                        key={nota.id || index}
                        className={selectedIds.includes(nota.id) ? 'bg-blue-50' : ''}
                      >
                        {podeAlterarCargaHorariaMassa && (
                          <td data-col="selecao" className="text-center">
                            <button
                              type="button"
                              onClick={() => toggleSelectItem(nota.id)}
                              className="p-1 hover:bg-gray-200 rounded"
                            >
                              <FontAwesomeIcon 
                                icon={selectedIds.includes(nota.id) ? faCheckSquare : faSquare}
                                className={selectedIds.includes(nota.id) ? "text-blue-600" : "text-gray-400"}
                              />
                            </button>
                          </td>
                        )}
                        <td data-col="codigo" data-label="Código">{nota.codigoDisciplina}</td>
                        <td data-col="extensao" data-label="Extensão do lançamento" className="mobile-truncate" title={nota.nomeExtensao || '-'}>
                          {nota.nomeExtensao || '-'}
                        </td>
                        <td data-col="curso" data-label="Curso" className="mobile-truncate" title={nota.nomeCurso || '-'}>
                          {nota.nomeCurso || '-'}
                        </td>
                        <td data-col="disciplina" data-label="Disciplina" className="mobile-truncate" title={nota.nomeDisciplina}>
                          {nota.nomeDisciplina}
                        </td>
                        <td data-col="carga" data-label="Carga Horária" className="desktop-only">
                          {nota.cargaHoraria ? `${nota.cargaHoraria}h` : '-'}
                        </td>
                        <td data-col="nota" data-label="Nota">
                          <span className={`nota-badge ${getNoteStatus(nota.nota)}`}>
                            {nota.nota !== null ? parseFloat(nota.nota).toFixed(2) : '-'}
                          </span>
                        </td>
                        <td data-col="lancada-em" data-label="Lançada em">
                          {formatarDataHora(nota.dataCadastro)}
                        </td>
                        <td data-col="lancada-por" data-label="Lançada por" className="mobile-truncate" title={tituloAutoria(nota)}>
                          {nota.pessoaCadastrou || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Modal de Alteração de Carga Horária em Massa - Apenas para quem tem permissão */}
      {podeAlterarCargaHorariaMassa && showCargaHorariaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                <FontAwesomeIcon icon={faClock} className="mr-2 text-blue-600" />
                Alterar Carga Horária
              </h3>
              <button
                onClick={() => setShowCargaHorariaModal(false)}
                className="text-gray-400 hover:text-gray-600"
                disabled={salvandoCargaHoraria}
              >
                <FontAwesomeIcon icon={faTimes} size="lg" />
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-4">
                Você está alterando a carga horária de <strong>{selectedIds.length}</strong> disciplina(s).
              </p>
              <div className="mb-4">
                <label htmlFor="novaCargaHoraria" className="block text-sm font-medium text-gray-700 mb-1">
                  Nova Carga Horária (horas) *
                </label>
                <input
                  type="number"
                  id="novaCargaHoraria"
                  value={novaCargaHoraria}
                  onChange={(e) => setNovaCargaHoraria(e.target.value)}
                  min="1"
                  max="999"
                  placeholder="Ex: 40"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={salvandoCargaHoraria}
                  autoFocus
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-lg">
              <button
                type="button"
                onClick={() => setShowCargaHorariaModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400"
                disabled={salvandoCargaHoraria}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={salvarCargaHorariaMassa}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-blue-300"
                disabled={salvandoCargaHoraria || !novaCargaHoraria}
              >
                {salvandoCargaHoraria ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Salvando...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} />
                    Salvar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Data/hora do lançamento no formato brasileiro
function formatarDataHora(valor) {
  if (!valor) return '-';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return '-';
  return data.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Tooltip com o rastro completo: quem lançou e, se houve, quem alterou
function tituloAutoria(nota) {
  const partes = [];
  if (nota.pessoaCadastrou) {
    partes.push(`Lançada por ${nota.pessoaCadastrou} em ${formatarDataHora(nota.dataCadastro)}`);
  }
  if (nota.pessoaAlterou || nota.dataAlteracao) {
    partes.push(`Alterada por ${nota.pessoaAlterou || 'não informado'} em ${formatarDataHora(nota.dataAlteracao)}`);
  }
  return partes.length > 0 ? partes.join(' | ') : 'Sem registro de autoria';
}

// Função auxiliar para status da nota
function getNoteStatus(nota) {
  if (nota === null || nota === undefined) return 'sem-nota';
  const notaNum = parseFloat(nota);
  if (notaNum >= 6) return 'aprovado';
  if (notaNum >= 5) return 'recuperacao';
  return 'reprovado';
}

export default HistoricoNotas;
